import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API_URLS } from "./apis/urls";
import Api from "./utils/apiCall";

interface MasterBeaconState {
  data: any;
  loading: "idle" | "pending" | "fulfilled" | "rejected";
  error: string | null;
  action: "delete" | "add" | "find" | "list" | "status" | "update" | "assign" | "employee-list" | null;
}

const masterBeaconInitialState: MasterBeaconState = {
  data: null,
  loading: "idle",
  error: null,
  action: null,
};

export const getMasterBeacon: any = createAsyncThunk(
  "get/masterBeacon",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.MASTER_BEACON.FIND_ALL,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const addMasterBeacon: any = createAsyncThunk(
  "add/masterBeacon",
  async (payload: any, thunkAPI) => {
    try {
      const url = API_URLS.MASTER_BEACON.ADD;
      const sanitized = { ...payload };
      delete (sanitized as any).id;
      const response = await Api.post(url, sanitized);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const updateMasterBeacon: any = createAsyncThunk(
  "update/masterBeacon",
  async (payload: any, thunkAPI) => {
    try {
      const url = `${API_URLS.MASTER_BEACON.UPDATE}/${payload.id}`;
      const sanitized = { ...payload };
      // keep id in path only
      delete (sanitized as any).id;
      const response = await Api.post(url, sanitized);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const findMasterBeacon: any = createAsyncThunk(
  "find/masterBeacon",
  async (payload: any, thunkAPI) => {
    try {
      const url = `${API_URLS.MASTER_BEACON.FIND_BY_ID}/${payload.id}`;
      const response = await Api.post(url, {});
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const deleteMasterBeacon: any = createAsyncThunk(
  "delete/masterBeacon",
  async (payload: any, thunkAPI) => {
    try {
      const url = `${API_URLS.MASTER_BEACON.DELETE}/${payload.id}`;
      const response = await Api.post(url, {});
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const statusMasterBeacon: any = createAsyncThunk(
  "status/masterBeacon",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.MASTER_BEACON.CHANGE_STATUS,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const assignMasterBeacon: any = createAsyncThunk(
  "assign/masterBeacon",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.MASTER_BEACON.ASSIGN_MASTER_BEACON,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const getEmployeeBeaconAssignmentList: any = createAsyncThunk(
  "get/employeeBeaconAssignmentList",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.MASTER_BEACON.EMPLOYEE_BEACON_ASSIGNMENT_LIST,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);



const masterBeaconSlice = createSlice({
  name: "masterBeacon",
  initialState: masterBeaconInitialState,
  reducers: {
    clearMasterBeaconState: (state) => {
      state.data = null;
      state.loading = "idle";
      state.error = null;
      state.action = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // LIST
      .addCase(getMasterBeacon.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.action = "list";
        state.data = null;
      })
      .addCase(getMasterBeacon.fulfilled, (state, action) => {
        state.loading = "fulfilled";
        state.data = action.payload;
        state.action = "list";
      })
      .addCase(getMasterBeacon.rejected, (state, action) => {
        state.loading = "rejected";
        state.error = action.payload as string;
        state.action = "list";
        state.data = null;
      })
      // ADD
      .addCase(addMasterBeacon.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.action = "add";
        state.data = null;
      })
      .addCase(addMasterBeacon.fulfilled, (state, action) => {
        state.loading = "fulfilled";
        state.data = action.payload;
        state.action = "add";
      })
      .addCase(addMasterBeacon.rejected, (state, action) => {
        state.loading = "rejected";
        state.error = action.payload as string;
        state.action = "add";
        state.data = null;
      })
      // UPDATE
      .addCase(updateMasterBeacon.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.action = "update";
        state.data = null;
      })
      .addCase(updateMasterBeacon.fulfilled, (state, action) => {
        state.loading = "fulfilled";
        state.data = action.payload;
        state.action = "update";
      })
      .addCase(updateMasterBeacon.rejected, (state, action) => {
        state.loading = "rejected";
        state.error = action.payload as string;
        state.action = "update";
        state.data = null;
      })
      // FIND
      .addCase(findMasterBeacon.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.action = "find";
        state.data = null;
      })
      .addCase(findMasterBeacon.fulfilled, (state, action) => {
        state.loading = "fulfilled";
        state.data = action.payload;
        state.action = "find";
      })
      .addCase(findMasterBeacon.rejected, (state, action) => {
        state.loading = "rejected";
        state.error = action.payload as string;
        state.action = "find";
        state.data = null;
      })
      // DELETE
      .addCase(deleteMasterBeacon.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.action = "delete";
        state.data = null;
      })
      .addCase(deleteMasterBeacon.fulfilled, (state, action) => {
        state.loading = "fulfilled";
        state.data = action.payload;
        state.action = "delete";
      })
      .addCase(deleteMasterBeacon.rejected, (state, action) => {
        state.loading = "rejected";
        state.error = action.payload as string;
        state.action = "delete";
        state.data = null;
      })
      // STATUS
      .addCase(statusMasterBeacon.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.action = "status";
        state.data = null;
      })
      .addCase(statusMasterBeacon.fulfilled, (state, action) => {
        state.loading = "fulfilled";
        state.data = action.payload;
        state.action = "status";
      })
      .addCase(statusMasterBeacon.rejected, (state, action) => {
        state.loading = "rejected";
        state.error = action.payload as string;
        state.action = "status";
        state.data = null;
      })
      // ASSIGN
      .addCase(assignMasterBeacon.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.action = "assign";
        state.data = null;
      })
      .addCase(assignMasterBeacon.fulfilled, (state, action) => {
        state.loading = "fulfilled";
        state.data = action.payload;
        state.action = "assign";
      })
      .addCase(assignMasterBeacon.rejected, (state, action) => {
        state.loading = "rejected";
        state.error = action.payload as string;
        state.action = "assign";
        state.data = null;
      })
      // EMPLOYEE BEACON ASSIGNMENT LIST
      .addCase(getEmployeeBeaconAssignmentList.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.action = "employee-list";
        state.data = null;
      })
      .addCase(getEmployeeBeaconAssignmentList.fulfilled, (state, action) => {
        state.loading = "fulfilled";
        state.data = action.payload;
        state.action = "employee-list";
      })
      .addCase(getEmployeeBeaconAssignmentList.rejected, (state, action) => {
        state.loading = "rejected";
        state.error = action.payload as string;
        state.action = "employee-list";
        state.data = null;
      });
  },
});

export const { clearMasterBeaconState } = masterBeaconSlice.actions;
export default masterBeaconSlice.reducer;
